// src/modules/ComplianceDashboard.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { useComplianceManager } from '../hooks/useComplianceManager';
import { LocalAuthority, IncidentStatus, ActionStatus, TrainingRecord, TrainingStatus } from '../types/compliance';
import {
  AlertTriangle, CheckCircle, Clock, FileText,
  Filter, XCircle, Activity, Users, Calendar,
  Shield, Award, Crown, Lock, Unlock
} from 'lucide-react';

export function ComplianceDashboard() {
  const { incidents, tasks, residents, closeIncidentLoop } = useComplianceManager();
  const [filterCouncil, setFilterCouncil] = useState<LocalAuthority | 'ALL'>('ALL');

  // ─── Training Expiry Alerts ──────────────────────────────
  const [training, setTraining] = useState<TrainingRecord[]>([]);
  useEffect(() => {
    const stored = localStorage.getItem('rmacs_compliance_training');
    if (stored) setTraining(JSON.parse(stored));
  }, []);

  const expiring = training.filter(t => {
    const days = (new Date(t.expiryDate).getTime() - new Date().getTime()) / (1000*60*60*24);
    return days <= 30 && days > 0;
  });
  const expired = training.filter(t => new Date(t.expiryDate) < new Date());

  // ─── KPIs ──────────────────────────────────────────────────
  const now = new Date();
  const overdueTasks = tasks.filter(t =>
    t.status !== ActionStatus.COMPLETED && new Date(t.dueDate) < now
  );
  const totalOverdue = overdueTasks.length;
  const pendingDue = tasks.filter(t =>
    t.status !== ActionStatus.COMPLETED &&
    new Date(t.dueDate) >= now &&
    new Date(t.dueDate) <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
  ).length;
  const openIncidents = incidents.filter(i => i.status !== IncidentStatus.CLOSED).length;
  const complianceScore = 100;

  const filteredIncidents = useMemo(() => {
    if (filterCouncil === 'ALL') return incidents;
    return incidents.filter(inc => inc.resident?.localAuthority === filterCouncil);
  }, [incidents, filterCouncil]);

  const filteredTasks = useMemo(() => {
    if (filterCouncil === 'ALL') return tasks;
    return tasks.filter(t => t.resident?.localAuthority === filterCouncil);
  }, [tasks, filterCouncil]);

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      [IncidentStatus.OPEN]: 'bg-blue-100 text-blue-800',
      [IncidentStatus.INVESTIGATING]: 'bg-yellow-100 text-yellow-800',
      [IncidentStatus.AWAITING_NOTIFICATIONS]: 'bg-orange-100 text-orange-800',
      [IncidentStatus.CLOSED]: 'bg-green-100 text-green-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const actionStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      [ActionStatus.PENDING]: 'bg-gray-200 text-gray-700',
      [ActionStatus.IN_PROGRESS]: 'bg-blue-100 text-blue-800',
      [ActionStatus.COMPLETED]: 'bg-green-100 text-green-800',
      [ActionStatus.OVERDUE]: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-lg border ${totalOverdue > 0 ? 'bg-red-100 border-red-300 text-red-800' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-semibold">Overdue Actions</span>
          </div>
          <div className="text-3xl font-bold mt-2">{totalOverdue}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            <span className="font-semibold">Pending Due (7 days)</span>
          </div>
          <div className="text-3xl font-bold mt-2">{pendingDue}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            <span className="font-semibold">Open Incidents</span>
          </div>
          <div className="text-3xl font-bold mt-2">{openIncidents}</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5" />
            <span className="font-semibold">Compliance Score</span>
          </div>
          <div className="text-3xl font-bold mt-2">{complianceScore}%</div>
        </div>
      </div>

      {/* ─── Training Expiry Widget ──────────────────────────── */}
      {(expiring.length > 0 || expired.length > 0) && (
        <div className="bg-white p-4 rounded-lg border border-gray-200 col-span-4">
          <h4 className="font-bold text-[#123B36] mb-2">⚠️ Training Expiry Alerts</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {expired.length > 0 && (
              <div className="bg-red-50 p-2 rounded border border-red-200">
                <span className="text-red-600 font-semibold">Expired:</span>
                <span className="text-sm ml-2">{expired.map(t => t.staffName + ' – ' + t.courseName).join(', ')}</span>
              </div>
            )}
            {expiring.length > 0 && (
              <div className="bg-yellow-50 p-2 rounded border border-yellow-200">
                <span className="text-yellow-600 font-semibold">Expiring soon (≤30 days):</span>
                <span className="text-sm ml-2">{expiring.map(t => t.staffName + ' – ' + t.courseName).join(', ')}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Council Filter */}
      <div className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-200">
        <Filter className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium">Filter by Council:</span>
        <button
          onClick={() => setFilterCouncil('ALL')}
          className={`px-3 py-1 text-xs rounded ${filterCouncil === 'ALL' ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`}
        >
          All
        </button>
        {Object.values(LocalAuthority).map(la => (
          <button
            key={la}
            onClick={() => setFilterCouncil(la)}
            className={`px-3 py-1 text-xs rounded ${filterCouncil === la ? 'bg-[#123B36] text-white' : 'bg-gray-200'}`}
          >
            {la}
          </button>
        ))}
      </div>

      {/* Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Active Incidents */}
        <div className="bg-white rounded-2xl border border-[#E1DCCD] overflow-hidden">
          <div className="bg-[#F7F5EE] px-6 py-3 border-b border-[#E1DCCD] flex justify-between items-center">
            <h3 className="font-bold text-[#123B36]">Active Incident Response Room</h3>
            <span className="text-sm text-gray-500">{filteredIncidents.length} incidents</span>
          </div>
          <div className="divide-y divide-[#E1DCCD] max-h-96 overflow-y-auto">
            {filteredIncidents.length === 0 ? (
              <div className="p-6 text-center text-gray-400">No incidents found</div>
            ) : (
              filteredIncidents.map(inc => (
                <div key={inc.id} className="p-4 hover:bg-[#F7F5EE] transition">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-[#20302C]">{inc.incidentRef}</p>
                      <p className="text-sm text-[#6B7A76]">{inc.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusBadge(inc.status)}`}>
                          {inc.status}
                        </span>
                        <span className="text-xs text-gray-500">{inc.resident?.localAuthority}</span>
                      </div>
                    </div>
                    {inc.status !== IncidentStatus.CLOSED && (
                      <button
                        onClick={() => {
                          const signature = prompt('Enter manager signature:');
                          if (!signature) return;
                          const analysis = prompt('Root cause analysis:') || '';
                          const outcomes = prompt('Lessons learned:') || '';
                          closeIncidentLoop(inc.id, signature, analysis, outcomes);
                        }}
                        className="text-xs bg-[#123B36] text-white px-2 py-1 rounded hover:bg-[#0E2C28]"
                      >
                        Close Loop
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Tasks Table */}
        <div className="bg-white rounded-2xl border border-[#E1DCCD] overflow-hidden">
          <div className="bg-[#F7F5EE] px-6 py-3 border-b border-[#E1DCCD] flex justify-between items-center">
            <h3 className="font-bold text-[#123B36]">Master Compliance Calendar</h3>
            <span className="text-sm text-gray-500">{filteredTasks.length} tasks</span>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F5EE]">
                <tr>
                  <th className="p-3 text-left">Title</th>
                  <th className="p-3 text-left">Owner</th>
                  <th className="p-3 text-left">Due Date</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.length === 0 ? (
                  <tr><td colSpan={5} className="p-6 text-center text-gray-400">No tasks</td></tr>
                ) : (
                  filteredTasks.map(task => {
                    const isOverdue = new Date(task.dueDate) < now && task.status !== ActionStatus.COMPLETED;
                    const displayStatus = isOverdue ? ActionStatus.OVERDUE : task.status;
                    return (
                      <tr key={task.id} className="border-b border-[#E1DCCD] hover:bg-[#F7F5EE] transition">
                        <td className="p-3">{task.title}</td>
                        <td className="p-3">{task.assignedOwner}</td>
                        <td className="p-3">{new Date(task.dueDate).toLocaleDateString()}</td>
                        <td className="p-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${actionStatusBadge(displayStatus)}`}>
                            {displayStatus}
                          </span>
                          {isOverdue && <AlertTriangle className="w-4 h-4 text-red-600 inline ml-1" />}
                        </td>
                        <td className="p-3 text-xs">{task.auditEvidencePortfolioTarget || 'None'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}